import {createApi} from '@reduxjs/toolkit/query/react'
import {addDays} from 'date-fns/addDays'
import {differenceInDays} from 'date-fns/differenceInDays'
import {format} from 'date-fns/format'
import {parseISO} from 'date-fns/parseISO'

import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Team
} from '@vio/core/src/analytics/types/Events'
import {numberOfAdults as getNumberOfAdultsFromConfig} from '@vio/core/src/room/business/roomConfiguration'
import {fetchBaseQuery} from '@vio/core/src/utils/network/fetchBaseQuery'

import {isInsightsDataValid} from '../utils/validation'

const PRICE_ANALYTICS_API_BASE_URL =
  process.env.NEXT_PUBLIC_PRICE_ANALYTICS_API_ENDPOINT

const getErrorType = (status?: number) => {
  switch (status) {
    case 400:
      return 'bad_request'
    case 500:
      return 'server_error'
    case 503:
      return 'service_unavailable'
    default:
      return 'unknown'
  }
}

// Calendar date range configuration
const CALENDAR_DAYS_BEFORE_CHECK_IN = 14
const CALENDAR_DAYS_AFTER_CHECK_IN = 14

export interface PriceTrendsParams {
  hotelId: string | number
  checkIn: string
  checkOut: string
  rooms: string
  currency: string
}

export interface PricePredictionParams {
  hotelId: string | number
  currency: string
  fromCheckIn: string
  toCheckIn: string
  roomConfiguration: string
  nights: number
}

export interface PriceTrendsResponse {
  medianHotelPrice: number
  timeRangeDays: number
  lastMedianSimilarHotelsPricePoint: number | null
  hotelChangePercentageThreshold: number
  marketChangePercentageThreshold: number
  marketPriceTrendPercentage: number | null
}

export interface DetailedPrice {
  mean: number
  median: number
  p10: number
  p35: number
  p65: number
  p90: number
}

export interface PriceHistoryDataPoint {
  timestamp: string
  price: number // Total price (when showTotalPrices is true)
  // Derived client-side in usePriceHistory (not provided by API)
  nightlyPrice?: number
  // Only present on similar_hotels_history points (from BE detailed_price)
  detailedPrice?: DetailedPrice
}

export interface PriceTrendsHistoryResponse {
  timeRangeDays: number
  currency: string
  rooms: string
  checkIn: string
  checkOut: string
  hotelId: string
  similarHotelsIds: string[]
  hotelHistory: PriceHistoryDataPoint[]
  similarHotelsHistory: PriceHistoryDataPoint[]
}

export interface PriceCalendarPoint {
  checkIn: string
  price: number
}

export interface PriceCalendarResponse {
  prices: PriceCalendarPoint[]
}

export const priceInsightsApi = createApi({
  reducerPath: 'priceInsightsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: PRICE_ANALYTICS_API_BASE_URL!
  }),
  endpoints: builder => ({
    // Price Trends endpoints (v1 API - GET queries)
    getPriceTrends: builder.query<PriceTrendsResponse, PriceTrendsParams>({
      query: params => ({
        url: 'v1/price-insights/trends',
        method: 'GET',
        params: {
          hotel_id: params.hotelId,
          check_in: params.checkIn,
          check_out: params.checkOut,
          room_configuration: getNumberOfAdultsFromConfig(params.rooms),
          currency: params.currency
        }
      }),
      // Transform to follow frontend naming convention
      transformResponse: (response: {
        median_hotel_price: number
        time_range_days: number
        last_median_similar_hotels_price_point: number | null
        hotel_change_percentage_threshold: number
        market_change_percentage_threshold: number
        market_price_trend_percentage: number | null
      }): PriceTrendsResponse => ({
        medianHotelPrice: response.median_hotel_price,
        timeRangeDays: response.time_range_days,
        lastMedianSimilarHotelsPricePoint:
          response.last_median_similar_hotels_price_point,
        hotelChangePercentageThreshold:
          response.hotel_change_percentage_threshold,
        marketChangePercentageThreshold:
          response.market_change_percentage_threshold,
        marketPriceTrendPercentage: response.market_price_trend_percentage
      }),
      async onQueryStarted(params, {queryFulfilled, dispatch}) {
        try {
          const {data} = await queryFulfilled

          if (!isInsightsDataValid(data)) {
            dispatch(
              trackEvent({
                category: Category.System,
                entity: Entity.PriceInsights,
                action: Action.Errored,
                team: Team.Retention,
                payload: {
                  errorType: 'no_data',
                  statusCode: 200,
                  requestParams: params,
                  responseData: data
                }
              })
            )
          }
        } catch (err) {
          const {error} = err as {
            error?: {status?: number; data?: {message?: string}}
          }

          dispatch(
            trackEvent({
              category: Category.System,
              entity: Entity.PriceInsights,
              action: Action.Errored,
              team: Team.Retention,
              payload: {
                errorType: getErrorType(error?.status),
                statusCode: error?.status,
                message: error?.data?.message,
                requestParams: params
              }
            })
          )
        }
      }
    }),
    getPriceHistory: builder.query<
      PriceTrendsHistoryResponse,
      PriceTrendsParams
    >({
      query: params => ({
        url: 'v1/price-insights/history',
        method: 'GET',
        params: {
          hotel_id: params.hotelId,
          check_in: params.checkIn,
          check_out: params.checkOut,
          room_configuration: getNumberOfAdultsFromConfig(params.rooms),
          currency: params.currency
        }
      }),
      // Transform to follow frontend naming convention
      transformResponse: (response: {
        time_range_days: number
        currency: string
        room_configuration: string
        check_in: string
        check_out: string
        hotel_id: string
        similar_hotels_ids: string[]
        hotel_history: Array<{
          timestamp: string
          price: number
        }>
        similar_hotels_history: Array<{
          timestamp: string
          price: number
          detailed_price?: {
            mean: number
            median: number
            p10: number
            p35: number
            p65: number
            p90: number
          }
        }>
      }): PriceTrendsHistoryResponse => ({
        timeRangeDays: response.time_range_days,
        currency: response.currency,
        rooms: response.room_configuration,
        checkIn: response.check_in,
        checkOut: response.check_out,
        hotelId: response.hotel_id,
        similarHotelsIds: response.similar_hotels_ids,
        hotelHistory: response.hotel_history.map(point => ({
          timestamp: point.timestamp,
          price: point.price
        })),
        similarHotelsHistory: response.similar_hotels_history.map(point => ({
          timestamp: point.timestamp,
          price: point.price,
          detailedPrice: point.detailed_price
        }))
      })
    }),
    getPriceCalendar: builder.query<PriceCalendarResponse, PriceTrendsParams>({
      query: params => {
        // Parse dates using date-fns
        const checkInDate = parseISO(params.checkIn)
        const checkOutDate = parseISO(params.checkOut)

        // Calculate nights from checkIn and checkOut dates
        const nights = differenceInDays(checkOutDate, checkInDate)

        // Calculate date range: configurable days before and after the original checkIn
        const calendarFromDate = addDays(
          checkInDate,
          -CALENDAR_DAYS_BEFORE_CHECK_IN
        )
        const calendarToDate = addDays(
          checkInDate,
          CALENDAR_DAYS_AFTER_CHECK_IN
        )

        return {
          url: 'v1/price-insights/calendar',
          method: 'GET',
          params: {
            hotel_id: params.hotelId,
            from_check_in: format(calendarFromDate, 'yyyy-MM-dd'),
            to_check_in: format(calendarToDate, 'yyyy-MM-dd'),
            room_configuration: getNumberOfAdultsFromConfig(params.rooms),
            nights: nights,
            currency: params.currency
          }
        }
      },
      // Transform to follow frontend naming convention
      transformResponse: (response: {
        prices: Array<{check_in: string; price: number}>
      }): PriceCalendarResponse => ({
        prices: response.prices.map(point => ({
          checkIn: point.check_in,
          price: point.price
        }))
      })
    }),
    getPricePrediction: builder.query<
      PriceCalendarResponse,
      PricePredictionParams
    >({
      query: params => {
        return {
          url: 'v1/price-insights/calendar',
          method: 'GET',
          params: {
            hotel_id: params.hotelId,
            from_check_in: params.fromCheckIn,
            to_check_in: params.toCheckIn,
            room_configuration: params.roomConfiguration,
            nights: params.nights,
            currency: params.currency
          }
        }
      },
      // Transform to follow frontend naming convention
      transformResponse: (response: {
        prices: Array<{check_in: string; price: number}>
      }): PriceCalendarResponse => ({
        prices: response.prices.map(point => ({
          checkIn: point.check_in,
          price: point.price
        }))
      })
    })
  })
})

export const {
  useGetPriceTrendsQuery,
  useGetPriceHistoryQuery,
  useGetPriceCalendarQuery,
  useGetPricePredictionQuery
} = priceInsightsApi
